//@ts-check
import { useCallback, useState, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from '@hookform/error-message';

import { endPoint } from "src/AppConstants";
import { useI18n } from "components/lni18n";
import FileIcon from "components/FileIcons/FileIcon";
import FileUpload from "components/FileUpload/FileUpload";
import LnIcon from "components/LnIcon";
import TextEditor from "components/TextEditor/HtmlTextEditor";
import useTypedSelector from "utils/useTypedSelector";
import classes from './mail.module.scss';
import { debouncer } from "src/utils/debouncer";

/**
 * MailEditor
 * @module features/classes/ClassItems/ClassMail/MailEditor
 * @return a feed form.
 * @param {{
    fileUploaded: any,
    handleFileDelete: Function,
    mailData:any,
    showNote: boolean,
    updateStatus: Function,
}} props
 */
const MailEditor = ({ fileUploaded, handleFileDelete, mailData, showNote, updateStatus }) => {

    const { languageService: t } = useI18n();
    const mailconfig = useTypedSelector(state => state.config.mailConfig);
    const user = useTypedSelector(state => state.login.user);

    const useImages = user.CanSwitchUser ? " image" : "";

    const saveDebounce = useRef(undefined);

    useEffect(() => {
        saveDebounce.current = new debouncer(updateStatus, 500);
        return () => saveDebounce.current?.clear();
    }, [])

    const editorSettings = {
        menubar: false,
        ln_custom: {
            useImages: user.CanSwitchUser
        },
        plugins: "visualblocks code link autolink table lists autoresize",

        browser_spellcheck: true,
        contextmenu: false,
        toolbar: "undo redo | styleselect | table | bold italic  | bullist numlist outdent indent | link code" + useImages,
        valid_elements: "@[id|data*],-h1,-h2,-h3,-h4,-h5,-strong/b,-em/i,#p," +
            "-div[class],br,table[class],tr[class],td[class],thead[class],tbody[class],th[class],tfoot[class],-span" +
            ",-ol,-ul,-li,dl,dt,hr,del/strike,ins,rp,rt,ruby,-small,sub,sup,-code,-blockquote" +
            ",-a[href|target=_blank],img[class|src|alt|title|width|height|align|data-id]",
    }

    const fileOptions = {
        AcceptFileTypes: mailconfig.fileTypes, MaxFileSize: mailconfig.maxFileSize
    };
   

    const initialValues = {
        MailBody: mailData.MailBody || '',
        MailSubject: mailData.MailSubject || '',
        Note: undefined
    }

    if (showNote) {
        initialValues['Note'] = mailData.Note || '';
    }


    const { subscribe, control, handleSubmit, register, formState: { errors, isDirty, isValid }, getValues } = useForm({
        defaultValues: initialValues,
        mode: "onChange"
    });

    useEffect(() => {
        const callback = subscribe({
            formState: {
                values: true,
                isValid: true,
                isDirty: true
            },
            callback: ({ values, isValid, isDirty }) => {
                if (saveDebounce.current) saveDebounce.current.debounce(values, isValid, isDirty);
            },
        })

        return () => callback()
    }, [subscribe]);


     /**
     *
     * @param {{key:string, name:string, size:number, type:string}} f
     */
    const handleFileUploadReady = useCallback( (f) => {
        fileUploaded(f);
        const values = getValues();
        saveDebounce.current.debounce(values, isValid, isDirty);

    }, [isValid, isDirty]);




    return (

        <div className="d-flex flex-column">
            <form className={["mb-5", classes['mail-form']].join(' ')} onSubmit={handleSubmit((d) => {
                console.log(d);
            })}>


                <div className="form-group mb-4">

                    <label htmlFor="MailSubject">{t.getText('header')}</label>
                    <input  {...register("MailSubject", { required: t.getText("emptyheaderwarning")  })} type="text" className="form-control" />
                    <ErrorMessage as="div" errors={errors} name="MailSubject" className="errorMessage" />
                </div>

                <div className="form-group mb-4">
                    <label htmlFor="MailBody">{t.getText('message')}</label>

                    <Controller
                        control={control}
                        rules={{ required: t.getText("emptybodywarning") }}
                        name="MailBody"
                        render={({ field: { onChange } }) => (
                            <TextEditor name="MailBody" iframe={true} onChange={onChange}
                                editorSettings={editorSettings} initialValue={initialValues.MailBody} />
                        )}
                    />
                    <ErrorMessage as="div" errors={errors} name="MailBody" className="errorMessage" />

                </div>


                {showNote && <div className="form-group mb-4">
                    <label htmlFor="Note">{t.getText('notes')} <br /><small>{t.getText('mail.notes.not.in.mail')}</small></label>
                    <textarea {...register("Note")} className="form-control" rows={3}></textarea>
                </div>}
                <FileUpload imagePreviewOptions={[{ name: "small", maxheight: 100, maxwidth: 100 }]} handleUploadReady={handleFileUploadReady} accept={fileOptions.AcceptFileTypes} languageService={t} maxsize={fileOptions.MaxFileSize} >
                    <div className="d-flex flex-column">
                        {mailData.Attachments.map(f => {
                            return (
                                <div key={f.Id} className="d-flex justify-content-between">
                                    <a className="mb-4" href={endPoint.API_HOST + f.Url}>
                                        <FileIcon className={[classes['file-icon']].join(' ')} size="512px" name={(f.Name || '').split('.').pop()} />
                                        <span>
                                            {f.Name}

                                        </span>
                                    </a>
                                    <button type="button" className="btn btn-link" title={t.getText('delete')} onClick={() => handleFileDelete(f.Id)}>
                                        <LnIcon className={['ml-4 small', classes['icon']].join(' ')} name="delete" />

                                    </button>
                                </div>
                            )

                        })}
                    </div>

                </FileUpload>
            </form>
        </div>

    )

}
export default MailEditor;

